import React from 'react';

const formatCurrency = (val) => {
  if (val == null || isNaN(val)) return '$0';
  return `$${Number(val).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const RentToOwnSummary = ({ investorResult, borrowerResult, audience }) => {
  if (!investorResult || !borrowerResult) {
    return <div className="text-center text-gray-500">No data available for Rent-to-Own.</div>;
  }

  return (
    <div className="bg-white shadow-md rounded-lg p-0 w-full">
      <h2 className="text-2xl font-bold text-center text-blue-700 mb-6">Rent-to-Own Summary</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 px-2">
        {/* Investor Summary */}
        <div className="bg-gray-50 rounded-md p-4 md:p-5">
          <h3 className="text-blue-800 text-xl font-semibold text-center mb-4">Investor Summary</h3>
          <div className="grid gap-y-2 text-sm" style={{ display: 'grid', gridTemplateColumns: '1fr auto' }}>
            <div>Capital Invested:</div>
            <div className="font-semibold">{formatCurrency(investorResult.originalPropertyValue)}</div>

            <div>Total Rent:</div>
            <div className="font-semibold">{formatCurrency(investorResult.totalRentCollected)}</div>

            <div>Total Equity Bought Out by Entrepreneur:</div>
            <div className="font-semibold">{formatCurrency(investorResult.totalEquityBuyout)}</div>

            <div>Total Expenses (Tax + Insurance + Maintenance):</div>
            <div className="font-semibold">{formatCurrency(investorResult.totalExpenses)}</div>

            <div>Net Cashflow (Rent - Expenses):</div>
            <div className="font-semibold">{formatCurrency(investorResult.netCashflow)}</div>

            <div>Final Asset Value:</div>
            <div className="font-semibold">{formatCurrency(investorResult.finalValue)}</div>

            <div>Exit Value (Cashflow + Equity + Appreciation - Sell Cost):</div>
            <div className="font-semibold">{formatCurrency(investorResult.exitValue)}</div>

            <div>Cash-on-Cash Return:</div>
            <div className="font-semibold">{investorResult.cashOnCashReturn}%</div>

            <div>Total ROI:</div>
            <div className="font-semibold">{investorResult.totalROI}%</div>

            <div>Annualized ROI:</div>
            <div className="font-semibold">{investorResult?.annualizedROI || 0}%</div>

            <div>IRR Estimate:</div>
            <div className="font-semibold">{investorResult?.irrEstimate || 0}%</div>

            <div>Annual NOI:</div>
            <div className="font-semibold">{formatCurrency(investorResult.annualNOI)}</div>

            <div>Cap Rate:</div>
            <div className="font-semibold">{investorResult.capRate}%</div>

            <div>Investment Term:</div>
            <div className="font-semibold">{investorResult.termYears} years</div>
          </div>

          <p className="text-xs text-gray-600 mt-4 text-center">
            * Exit value includes rent income, equity payback, and appreciation. Expenses reduce net cashflow. Values are after closing cost deduction.
          </p>

        </div>
        {/* Entrepreneur Summary */}
        <div className="bg-purple-50 rounded-md p-4 md:p-5 flex flex-col justify-between">
          <div>
            <h3 className="text-purple-700 text-xl font-semibold text-center mb-4">Entrepreneur Summary</h3>
            <div className="grid gap-y-2 text-sm" style={{ display: 'grid', gridTemplateColumns: '1fr auto' }}>
              <div>Average Monthly Payment (Rent only):</div>
              <div className="font-semibold">{formatCurrency(borrowerResult.monthlyRentPayment)}</div>

              <div>Average Monthly Payment (Rent + Equity):</div>
              <div className="font-semibold">{formatCurrency(borrowerResult.monthlyPayment)}</div>

              <div>Rent Paid Over Time:</div>
              <div className="font-semibold">{formatCurrency(borrowerResult.totalRentPaid)}</div>

              <div>Equity Paid Over Time:</div>
              <div className="font-semibold">{formatCurrency(borrowerResult.totalBuyout)}</div>

              <div>Total Expenses (Tax + Insurance + Maintenance):</div>
              <div className="font-semibold">{formatCurrency(borrowerResult.totalExpenses)}</div>

              <div>Total Paid (Rent + Equity + Expenses):</div>
              <div className="font-semibold">{formatCurrency(borrowerResult.totalPayment)}</div>

              <div>Total Equity:</div>
              <div className="font-semibold">{formatCurrency(borrowerResult.equityBuilt)}</div>

              <div>Exit Value (Asset × Ownership − Expenses − Sell Cost):</div>
              <div className="font-semibold">{formatCurrency(borrowerResult.exitValue)}</div>

              <div>Investment Term:</div>
              <div className="font-semibold">{borrowerResult.termYears} years</div>
            </div>
          </div>
          <p className="text-xs text-gray-600 mt-3 text-center leading-snug">
            * Total paid includes all rent and equity contributions. Equity builds monthly and determines final ownership-based asset share.
          </p>
        </div>
      </div>

    </div>
  );
};

export default RentToOwnSummary;
